import {fromJS, Map} from 'immutable';
import {FILTERS, STATE} from '../constants/index';
import {isArrayNotEmpty, filterObjectsByKeyValues, filterObjectsByMultipleKeyValues, sortArrayOfObjectsByKey, sortArrayOfObjectsByKeyPath} from '../utils/js/array';
import {get} from '../utils/js/object';
import {types} from '../utils/js/types';

const SORT_ID = 'sort_id';

/**
 * @private
 * @desc Get items and segmentsIsActive data used by several case statements in the filters reducer
 * @param {Object} data immutable filters ui object
 * @param {Array} filters
 * @param {Boolean} [isToggle]
 * @returns {{segmentsIsActive: Boolean, segmented: Array, filtered: Array}}
 */
function getItemsAndSegmentsIsActive(data, filters, isToggle = false) {
  let segmentsIsActive = data.get('segmentsIsActive');
  if (isToggle) segmentsIsActive = !segmentsIsActive;
  const segmented = data.getIn(['items', 'segmented']);
  const itemsToFilter = segmentsIsActive ? segmented : data.getIn(['items', 'all']);
  const filtered = fromJS(filterObjectsByMultipleKeyValues(itemsToFilter.toJS(), filters));
  return {segmentsIsActive, segmented, filtered};
}

/**
 * @private
 * @desc Find the previously set filter object to update in the array of new action.filters
 * @param {Array} nextFilters
 * @param {Object} prevFilter
 * @returns {Object|null}
 */
function getNextFilterByPrevFilter(nextFilters, prevFilter) {
  return nextFilters.filter(item => item.keyPath === prevFilter.keyPath)[0] || null;
}

/**
 * @private
 * @desc Used to ensure the object received from getNextFilterByPrevFilter is valid
 * @param {Object} filter
 * @returns {Boolean}
 */
function filterIsValid(filter) {
  return (filter !== null && types(filter.values).isDefined && filter.values !== null);
}

/**
 * @private
 * @desc Look at a filter object's values and increment the numFiltersSelected counter accordingly
 * @param ct
 * @param filter
 * @returns {Number}
 */
function incrementNumFiltersSelected(ct, filter) {
  return ct + (types(filter.values).isArray ? filter.values.length : 1);
}

/**
 * @private
 * @desc Determine if a filter object available item isChecked based on filter values provided
 * @param {Object} opts {isRadio, itemValue, radioValue, values}
 * @returns {Boolean}
 */
function getAvailableIsChecked(opts) {
  const {isRadio, itemValue, radioValue, values} = opts;
  const type = types(values);
  return isRadio ? (radioValue === itemValue) : (
    (isArrayNotEmpty(values) && (values.indexOf(itemValue) !== -1)) ||
    ((type.isString || type.isNumber || type.isBoolean) && itemValue === values) ||
    (type.isFunction && values(itemValue))
  );
}

/**
 * @private
 * @desc Determine if a filter object available item isVisible if its value matches that of a segmented array item
 * @param {Object} opts {segmentsIsActive = false, segmented = [], keyPath, value}
 * @returns {Boolean}
 */
function getAvailableIsVisible(opts) {
  const {segmentsIsActive = false, segmented = [], keyPath, value} = opts;
  return segmentsIsActive ? segmented.some(item => get(item, keyPath) === value) : true;
}

/**
 * @private
 * @desc Iterator to apply checked values on each available array item
 * @param item {Object} A filter[i].available[i] or filter[i].available[i].groupingAvailable[i] object
 * @param opts {Object} {isRadio, radioValue, values, reset}
 * @returns {Object}
 */
function setNextFiltersAvailableIterator(item, opts) {
  const {isRadio, radioValue, values, reset} = opts;
  return Object.assign(item, (
    types(item.groupingAvailable).isDefined ?
      {groupingAvailable: item.groupingAvailable.map(item => setNextFiltersAvailableIterator(item, opts))} :
      {checked: reset ? false : getAvailableIsChecked({isRadio, itemValue:item.value, radioValue, values})}
  ));
}

/**
 * General action handling for filter state updates
 * @param {Object} state An immutable state object to check against
 * @param {Object} action Data to update the redux store state
 * @returns {Object} A (usually changed) immutable state object
 */
export default function (state = Map(), action = null) {
  switch (action.type) {
    case FILTERS.COMPOSE_FILTERS_UI_SHAPE: {
      const { items, filters:inputFilter, segments:inputSegment } = action;
      action.items.forEach((item, i) => Object.assign(item, {[SORT_ID]: i}));
      // start a new segments arr and only add to it if segment is valid
      // keep a flatter array of just unique ids to check against
      // segmented will contain the OR logic for items
      const segments = [];
      const segmented_ids = [];
      const segmented = (!isArrayNotEmpty(action.segments)) ? action.items :
        sortArrayOfObjectsByKey(
          action.segments.reduce((segmented, segment) => {
            // get items that match this segment
            const possible = filterObjectsByKeyValues(action.items, segment.keyPath, segment.values);
            // only proceed if we have possible items
            if (possible.length !== 0) {
              // add valid segment
              segments.push(segment);
              // loop through all possible and only push to segmented if it does not already exist
              possible.forEach(possibleItem => {
                if (segmented_ids.indexOf(possibleItem[SORT_ID]) === -1) {
                  segmented_ids.push(SORT_ID);
                  segmented.push(possibleItem);
                }
              });
            }
            return segmented;
          }, []),
          SORT_ID
        );
      // if segments.length, start them in active state
      const segmentsIsActive = segments.length !== 0;

      // now determine filter form structure by iterating over the incoming Filters_Config
      const filters = action.filters.reduce((next, filter) => {
        const {category, keyPath, groupingPath} = filter;
        // keypath = remapped value 
        const availableArr = action.items.reduce((availableArr, item) => {
          const value = get(item, keyPath);
          const groupingValue = get(item, groupingPath || '');
          if (types(value).isDefined && availableArr.filter(item => {
            // if an item with that keypath is not in the collection already, add it
            return item.value === value;
          }).length === 0) availableArr.push({value, groupingValue});
          return availableArr;
        }, []);
        // proceed if there is more than value at keyPath in the availableArr
        if (availableArr.length > 1) {
          let isVisibleCt = 0;
          // isGrouped will be derived based on if groupingPath is provided in action
          let isGrouped = false;
          // these are the groupings available
          let available = availableArr.map(item => {
            const {value, groupingValue} = item;
            const label = action.labelFn ? action.labelFn(value, {keyPath}): value;
            const isVisible = getAvailableIsVisible({segmentsIsActive, segmented, keyPath, value});
            if (isVisible) isVisibleCt++;
            const o = {label, value, isVisible, checked:false};
            // if a groupingValue exists at groupingPath then we can use grouping for this filter
            if (types(groupingValue).isDefined) {
              isGrouped = true;
              return Object.assign(o, {groupingValue});
            }
            return o;
          });
          // create a filter fieldset object to push to filters
          // each fieldset is an item in the shaped filters array (i.e. shape.filters[])
          const fieldset = {category, keyPath, isVisible:(isVisibleCt > 1), values: null};
          // if grouping, we must re-iterate through the available array to create the grouping sections
          // in newly composed available shape
          if (isGrouped) {
            available = available.reduce((nextAvailable, item) => {
              const {groupingValue} = item;
              if (types(groupingValue).isDefined) {
                const groupingLabel = action.labelFn ? action.labelFn(groupingValue, {groupingPath}): groupingValue;
                // try to get an existing group from nextAvailable
                const group = nextAvailable.find(item => item.groupingValue === groupingValue);
                // if we do not have an existing group, create one with this item
                // as the first index of groupingAvailable
                if (!group) {
                  delete item.groupingValue;
                  const newGroup = {groupingValue, groupingLabel, groupingAvailable: [item]};
                  nextAvailable.push(newGroup);
                // else, add the item to the existing group's groupingAvailable
                } else {
                  group.groupingAvailable.push(item);
                }
              }
              return nextAvailable;
            }, []);
            fieldset.isGrouped = true;
          }
          fieldset.available = available;
          next.push(fieldset);
        }
        // next filter
        return next;
      }, []);


      // get the final object shape to add to redux store
      const shape = {
        items: {
          all: action.items,
          segmented,
          filtered: segmented
        },
        // a placeholder; filled in by SET_NEXT_FILTERED_UI
        nextFilters: {},
        filters,
        segments,
        segmentsIsActive,
        // if segmentsIsActive, then toggle should be visible to start
        segmentsToggleIsVisible: segmentsIsActive,
        numFiltersSelected: 0,
        filtersIsVisible: filters.some(filter => filter.isVisible)
      };
      return state.setIn(STATE.UI.FILTERS.concat([action.storeKey]), fromJS(shape));
    }

    case FILTERS.SET_NEXT_FILTERS_UI: {
      const data = state.getIn(STATE.UI.FILTERS.concat([action.storeKey]), null);
      if (data !== null) {
        return state.setIn(STATE.UI.FILTERS.concat([action.storeKey, 'nextFilters']), fromJS({
          filters: action.filters,
          filteredLength: getItemsAndSegmentsIsActive(data, action.filters).filtered.size,
          numFiltersSelected: data.get('filters').toJS().reduce((ct, filter) => {
            const nextFilter = getNextFilterByPrevFilter(action.filters, filter);
            return filterIsValid(nextFilter) ? incrementNumFiltersSelected(ct, nextFilter) : ct;
          }, 0)
        }));
      }
      return state;
    }

    case FILTERS.SET_FILTERS_UI_VALUES: {
      const storeKey = action.storeKey;
      let data = state.getIn(STATE.UI.FILTERS.concat([storeKey]), null);
      if (data !== null) {
        const {segmentsIsActive, segmented, filtered} = getItemsAndSegmentsIsActive(data, action.filters);

        let numFiltersSelected = 0;
        const nextFilters = data.get('filters').toJS().map(filter => {
          const nextFilter = getNextFilterByPrevFilter(action.filters, filter);
          let iteratorOpts;
          if (filterIsValid(nextFilter)) {
            const {values, radioValue} = nextFilter;
            numFiltersSelected = incrementNumFiltersSelected(numFiltersSelected, nextFilter);
            filter.values = values;
            iteratorOpts = {isRadio: types(radioValue).isDefined, radioValue, values};
          // actions.filters DOES NOT contain filters for this keyPath
          } else {
            filter.values = null;
            iteratorOpts = {reset: false};
            if (types(filter.radioValue).isDefined) delete filter.radioValue;
          }
          filter.available = filter.available.map(item => setNextFiltersAvailableIterator(item, iteratorOpts));
          return filter;
        });

        // we need to check what would happen if segments are active with current filters
        // if there are no results in that scenario, we hide the toggle
        if (!segmentsIsActive && data.get('segments').size !== 0) {
          const filteredIfSegmented = filterObjectsByMultipleKeyValues(segmented.toJS(), action.filters);
          data = data.set('segmentsToggleIsVisible', (filteredIfSegmented.length !== 0));
        }
        data = data.setIn(['items', 'filtered'], filtered);
        data = data.set('numFiltersSelected', numFiltersSelected);
        data = data.set('filters', fromJS(nextFilters));
        data = data.set('nextFilters', Map());
        return state.setIn(STATE.UI.FILTERS.concat([storeKey]), data);
      }
      return state;
    }

    case FILTERS.SORT_FILTERED_UI_VALUES: {
      const {storeKey, sortByKeyPath, isDesc = false, isNumber = false, inputValue = ''} = action;
      let data = state.getIn(STATE.UI.FILTERS.concat([storeKey]), null);
      if (data !== null) {
        const filtered = data.getIn(['items', 'filtered']);
        const sorted = sortArrayOfObjectsByKeyPath(filtered, sortByKeyPath, isDesc, isNumber);
        data = data.setIn(['items', 'filtered_sorted'], sorted);
        data = data.setIn(['sorting'], {sortByKeyPath, isDesc, isNumber, inputValue});
        return state.setIn(STATE.UI.FILTERS.concat([storeKey]), data);
      }
      return state;
    }

    case FILTERS.TOGGLE_SEGMENTS_UI: {
      const storeKey = action.storeKey;
      let data = state.getIn(STATE.UI.FILTERS.concat([storeKey]), null);
      if (data !== null) {
        const {segmentsIsActive, segmented, filtered} = getItemsAndSegmentsIsActive(data, data.get('filters').toJS(), true);

        // loop through filters and reset isVisible for checkboxes/fieldsets/form and numFiltersSelected selected
        const filters = data.get('filters').toJS();
        let totalFiltersSelected = 0;
        filters.forEach(filter => {
          let isVisibleCt = 0;
          let numFiltersSelected = 0;
          const iterator = o => {
            if (types(o.groupingAvailable).isDefined) {
              o.groupingAvailable.forEach(iterator);
            } else {
              const isVisible = getAvailableIsVisible({segmentsIsActive, segmented:segmented.toJS(), keyPath:filter.keyPath, value:o.value});
              o.isVisible = isVisible;
              if (isVisible) {
                isVisibleCt++;
                if (o.checked) numFiltersSelected++;
              }
            }
          };
          filter.available.forEach(iterator);
          filter.isVisible = isVisibleCt > 1;
          if (filter.isVisible) totalFiltersSelected = totalFiltersSelected + numFiltersSelected;
        });

        data = data.setIn(['items', 'filtered'], filtered);
        data = data.set('filters', fromJS(filters));
        data = data.set('numFiltersSelected', totalFiltersSelected);
        data = data.set('filtersIsVisible', filters.some(filter => filter.isVisible));
        data = data.set('segmentsIsActive', segmentsIsActive);
        return state.setIn(STATE.UI.FILTERS.concat([storeKey]), data);
      }
      return state;
    }

    // this has nothing to do with the filters UI; its used to store filtered data in redux instead
    // of having to run a filter util every time the store updates
    case FILTERS.SAVE_FILTERED_ARRAYS: {
      const storeKey = action.storeKey;
      if (storeKey) {
        if (isArrayNotEmpty(action.items) && types(storeKey).isString && isArrayNotEmpty(action.filters)) {
          action.filters.forEach(filter => {
            const filtered = filterObjectsByKeyValues(action.items, filter.oKeyPath, filter.value);
            state = state.setIn(STATE.DATA.FILTERS.concat([storeKey, filter.oKeyPath, filter.storeKey]), fromJS(filtered));
          });
        }
      }
      return state;
    }

    default:
      return state;
  }
}
